/**
 * `checkStatBlock` (1.Z-1 deliverable 5): what disagrees with the numbers a 2014 stat block
 * implies, as hints. A homebrew monster may break the rules on purpose, so nothing here refuses a
 * block. Warnings come in the field order of `StatBlockV1`, then list index, each code once per
 * path. Every check uses the table's proficiency for the rating, never the stored bonus.
 */
import { parseAttackLine } from '../parse/attack-line/index.js';
import { SKILL_ABILITIES } from '../schema/common.js';
import { abilityModifier, averageOfDice, hitDieForSize, proficiencyForChallenge, xpForChallenge, } from './tables.js';
/** Collects warnings, keeping each code once per path. */
export class WarningList {
    items = [];
    seen = new Set();
    add(code, path, expected, actual) {
        const key = `${code}\u0000${JSON.stringify(path)}`;
        if (this.seen.has(key))
            return;
        this.seen.add(key);
        this.items.push({ code, path, expected, actual });
    }
}
/** A damage part with dice and a stated average: the average must be the dice's, floored at 0. */
export function checkDamagePart(list, part, path) {
    if (!part?.dice || part.average === null)
        return;
    const expected = Math.max(0, averageOfDice(part.dice));
    if (part.average !== expected) {
        list.add('damage_average_mismatch', [...path, 'average'], expected, part.average);
    }
}
/**
 * Removes CommonMark emphasis markers (`*`, `_`, and a backslash escaping one) so that an attack
 * written as `*Melee Weapon Attack:* +4 to hit, …` reads as the plain sentence.
 */
export function stripEmphasis(text) {
    return text.replace(/\\([*_])/g, '$1').replace(/[*_]+/g, '');
}
const ATTACK_FIELDS = [
    'range',
    'kind',
    'toHit',
    'reach',
    'rangeNormal',
    'rangeLong',
    'target',
    'damage',
    'versatile',
    'riders',
    'hitText',
];
/** The first field where the text's attack differs from the stored one, or null when they agree. */
function firstDifference(parsed, stored) {
    for (const field of ATTACK_FIELDS) {
        if (JSON.stringify(parsed[field]) !== JSON.stringify(stored[field]))
            return field;
    }
    return null;
}
function checkAttack(list, ctx, text, attack, path) {
    if (attack === null)
        return;
    const parsed = parseAttackLine(stripEmphasis(text));
    if (!parsed.ok) {
        list.add('attack_text_disagrees', [...path, 'attack'], null, parsed.reason);
    }
    else {
        const field = firstDifference(parsed.attack, attack);
        if (field !== null)
            list.add('attack_text_disagrees', [...path, 'attack'], null, field);
    }
    const abilities = attack.kind === 'weapon' ? ['str', 'dex'] : ctx.spellAbilities;
    const accepted = abilities.map((a) => ctx.prof + ctx.mods[a]);
    if (!accepted.includes(attack.toHit)) {
        list.add('attack_bonus_unusual', [...path, 'attack', 'toHit'], Math.max(...accepted), attack.toHit);
    }
    attack.damage.forEach((part, j) => {
        checkDamagePart(list, part, [...path, 'attack', 'damage', j]);
    });
    checkDamagePart(list, attack.versatile, [...path, 'attack', 'versatile']);
    attack.riders.forEach((part, k) => {
        checkDamagePart(list, part, [...path, 'attack', 'riders', k]);
    });
}
/** 10 plus the Perception bonus: the listed Perception skill, else the Wisdom modifier. */
export function passivePerceptionOf(block) {
    const listed = block.skills.find((s) => s.skill === 'prc');
    return 10 + (listed ? listed.bonus : abilityModifier(block.abilities.wis));
}
const FEATURE_LISTS = ['traits', 'actions', 'bonusActions', 'reactions'];
export function checkStatBlock(block) {
    const list = new WarningList();
    const a = block.abilities;
    const mods = {
        str: abilityModifier(a.str),
        dex: abilityModifier(a.dex),
        con: abilityModifier(a.con),
        int: abilityModifier(a.int),
        wis: abilityModifier(a.wis),
        cha: abilityModifier(a.cha),
    };
    const rating = block.challenge.rating;
    const prof = proficiencyForChallenge(rating);
    const castingAbilities = [...new Set(block.spellcasting.map((s) => s.ability))];
    const ctx = {
        mods,
        prof,
        spellAbilities: castingAbilities.length > 0 ? castingAbilities : ['int', 'wis', 'cha'],
    };
    if (block.rules === '2024')
        list.add('rules_not_2014', ['rules'], '2014', '2024');
    const formula = block.hitPoints.formula;
    if (formula !== null) {
        const average = Math.max(1, averageOfDice(formula));
        if (block.hitPoints.average !== average) {
            list.add('hp_average_mismatch', ['hitPoints', 'average'], average, block.hitPoints.average);
        }
        const die = hitDieForSize(block.size);
        if (formula.die !== die) {
            list.add('hit_die_size_mismatch', ['hitPoints', 'formula', 'die'], die, formula.die);
        }
        // `+ 0` turns the −0 of 0 dice times a negative modifier into 0, which JSON keeps.
        const bonus = formula.count * mods.con + 0;
        if (formula.bonus !== bonus) {
            list.add('hp_bonus_unusual', ['hitPoints', 'formula', 'bonus'], bonus, formula.bonus);
        }
    }
    block.savingThrows.forEach((save, i) => {
        const expected = mods[save.ability] + prof;
        if (save.bonus !== expected) {
            list.add('save_bonus_unusual', ['savingThrows', i, 'bonus'], expected, save.bonus);
        }
    });
    block.skills.forEach((entry, i) => {
        const mod = mods[SKILL_ABILITIES[entry.skill]];
        if (entry.bonus !== mod + prof && entry.bonus !== mod + 2 * prof) {
            list.add('skill_bonus_unusual', ['skills', i, 'bonus'], mod + prof, entry.bonus);
        }
    });
    const passive = passivePerceptionOf(block);
    if (block.senses.passivePerception !== passive) {
        list.add('passive_perception_mismatch', ['senses', 'passivePerception'], passive, block.senses.passivePerception);
    }
    const xp = xpForChallenge(rating);
    const xpAccepted = rating === 0 ? [0, xp] : [xp];
    if (!xpAccepted.includes(block.challenge.xp)) {
        list.add('xp_mismatch', ['challenge', 'xp'], xp, block.challenge.xp);
    }
    if (block.proficiencyBonus !== prof) {
        list.add('proficiency_mismatch', ['proficiencyBonus'], prof, block.proficiencyBonus);
    }
    for (const key of FEATURE_LISTS) {
        block[key].forEach((feature, i) => {
            checkAttack(list, ctx, feature.text, feature.attack, [key, i]);
        });
    }
    if (block.legendary !== null) {
        const perRound = block.legendary.perRound;
        block.legendary.options.forEach((option, i) => {
            const path = ['legendary', 'options', i];
            if (option.cost > perRound) {
                list.add('legendary_cost_exceeds_actions', [...path, 'cost'], perRound, option.cost);
            }
            checkAttack(list, ctx, option.text, option.attack, path);
        });
    }
    if (block.legendaryResistance !== null &&
        !block.traits.some((t) => t.name.startsWith('Legendary Resistance'))) {
        list.add('legendary_resistance_trait_missing', ['legendaryResistance'], 'Legendary Resistance', null);
    }
    block.lair?.actions.forEach((feature, i) => {
        checkAttack(list, ctx, feature.text, feature.attack, ['lair', 'actions', i]);
    });
    block.spellcasting.forEach((casting, i) => {
        const mod = mods[casting.ability];
        const saveDc = 8 + prof + mod;
        const attackBonus = prof + mod;
        if (casting.saveDc !== null && casting.saveDc !== saveDc) {
            list.add('spell_save_dc_mismatch', ['spellcasting', i, 'saveDc'], saveDc, casting.saveDc);
        }
        if (casting.attackBonus !== null && casting.attackBonus !== attackBonus) {
            list.add('spell_attack_mismatch', ['spellcasting', i, 'attackBonus'], attackBonus, casting.attackBonus);
        }
    });
    return list.items;
}
//# sourceMappingURL=check-statblock.js.map